import { defineStore } from 'pinia'
import { array, number, object, type RefinementCtx, string, z, ZodIssueCode } from 'zod'

import { translationFunctionDefault } from '@/utilities/translations'

const useZodSchemaStore = defineStore('ZodSchemaStore', () => {
  const tfDefault = translationFunctionDefault()
  z.setErrorMap((issue, ctx) => {
    if (issue.code === ZodIssueCode.invalid_type && issue.received === 'undefined') {
      return { message: tfDefault('editRelationGroup.requiredInput') }
    }

    return { message: ctx.defaultError }
  })

  const emailSchema = object({
    email_ids: array(
      object({
        doctype: string().min(1, tfDefault('editRelationGroup.requiredInput')),
        email_id: string().email(tfDefault('editRelationGroup.invalidMail')),
        is_primary: number().min(0).max(1),
        tw_is_invoice_email: number().min(0).max(1).optional(),
      }),
    ),
  })

  const weblinkSchema = object({
    tw_weblinks: array(
      object({
        doctype: string().default('TW Weblink').catch('TW Weblink'),
        type: string().min(1, tfDefault('editRelationGroup.requiredInput')),
        url: string().url(tfDefault('editRelationGroup.invalidUrl')),
      }),
    ),
  })

  const singlePhoneNumberSchema = object({
    doctype: string().default('Contact Phone').catch('Contact Phone'),
    is_primary_phone: number().min(0).max(1),
    phone: string()
      .trim()
      .min(1, tfDefault('editRelationGroup.requiredInput'))
      .refine(
        (value: string | undefined) => {
          if (value) {
            const phoneRegEx = /^[+]?[0-9()\s/-]{1,24}$/u

            return phoneRegEx.test(value)
          }

          return false
        },
        {
          message: tfDefault('editRelationGroup.invalidPhoneNumber'),
        },
      ),
    tw_phone_type: string().min(1, tfDefault('editRelationGroup.requiredInput')),
  })

  const phoneNumberSchema = object({
    phone_nos: array(singlePhoneNumberSchema).default([]),
  })

  const generalDataPersonSchema = object({
    first_name: string().min(1, tfDefault('editRelationGroup.requiredInput')),
    last_name: string().min(1, tfDefault('editRelationGroup.requiredInput')),
    salutation: string().optional().nullable(),
    tw_contact_origin: string().optional().nullable(),
    tw_date_of_birth: string().date().optional().nullable().catch(null),
    tw_description: string().optional().nullable(),
  })

  const bankAccountSchema = object({
    is_default: number().min(0).max(1).default(0).catch(0),
    tw_bank: string().optional().catch(''),
    tw_bank_account_bic: string(),
    tw_bank_account_iban: string(),
    tw_bank_account_owner: string(),
  })

  type BankAccountSchemaType = z.infer<typeof bankAccountSchema>
  const bankAccountRefineMent = (values: BankAccountSchemaType, ctx: RefinementCtx): boolean => {
    // Bank data is optional, but once the user enters anything, require all fields.
    const hasAnyBankInput =
      !!values.tw_bank ||
      !!values.tw_bank_account_bic ||
      !!values.tw_bank_account_iban ||
      !!values.tw_bank_account_owner

    if (!hasAnyBankInput) {
      return true
    }

    if (!values.tw_bank_account_bic) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        fatal: true,
        message: tfDefault('editRelationGroup.requiredInput'),
        path: ['tw_bank_account_bic'],
      })
    }
    if (!values.tw_bank_account_iban) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        fatal: true,
        message: tfDefault('editRelationGroup.requiredInput'),
        path: ['tw_bank_account_iban'],
      })
    }
    if (!values.tw_bank_account_owner) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        fatal: true,
        message: tfDefault('editRelationGroup.requiredInput'),
        path: ['tw_bank_account_owner'],
      })
    }

    return true
  }

  const generalDataOrganizationSchema = object({
    customer_group: string().nullable().default(null),
    organization_name: string().min(1, tfDefault('editRelationGroup.requiredInput')),
    supplier_group: string().nullable().default(null),
    tax_id: string().default(''),
    tw_commercial_register: string().default(''),
    tw_contact_origin: string().default(''),
    tw_description: string().default(''),
    tw_industry_type: string().nullable().default(null),
    tw_vat_number: string().default(''),
  })

  const metaDataPersonSchema = object({
    tw_rating: string(),
    tw_responsible_employee: string().optional().nullable(),
  })

  const metaDataOrganizationSchema = metaDataPersonSchema.extend(
    object({ tw_pipeline_state: string().optional() }).shape,
  )

  const singleAddressSchema = object({
    address_line1: string().min(1, tfDefault('editRelationGroup.requiredInput')),
    address_line2: string().catch(''),
    address_title: string().min(1, tfDefault('editRelationGroup.requiredInput')).default(''),
    address_type: string().catch('Other'),
    city: string().min(1, tfDefault('editRelationGroup.requiredInput')),
    country: string().min(1, tfDefault('editRelationGroup.requiredInput')).default('Germany'),
    doctype: string().catch('Address'),
    is_primary_address: number().min(0).max(1).default(0).catch(0),
    name: string().optional(),
    pincode: string().min(1, tfDefault('editRelationGroup.requiredInput')).default(''),
    state: string().optional().catch(''),
    tw_alternative_recipient: string().catch(''),
  })

  const addressListSchema = object({
    tw_addresses: array(singleAddressSchema).optional(),
  })

  const primaryAddressSchema = object({
    tw_primary_address: singleAddressSchema.optional().nullable(),
  })

  type AddressFieldsType = z.infer<typeof addressListSchema> & z.infer<typeof primaryAddressSchema>

  const addressRefinement = (values: AddressFieldsType): boolean => {
    if ((!values.tw_addresses || values.tw_addresses.length === 0) && values.tw_primary_address) {
      values.tw_primary_address = null
    }
    if (values.tw_addresses && values.tw_addresses.length > 0) {
      const primary = values.tw_addresses.find((address) => {
        return address.is_primary_address === 1
      })

      values.tw_primary_address = primary
    }

    return true
  }

  const dynamicLinkListSchema = object({
    links: array(
      object({
        doctype: string().default('Dynamic Link').catch('Dynamic Link'),
        image: string().optional().nullable(),
        link_doctype: string().min(1, tfDefault('editRelationGroup.requiredInput')),
        link_name: string().min(1, tfDefault('editRelationGroup.requiredInput')),
        tw_description: string().optional(),
        tw_is_primary_link: number().min(0).max(1).optional(),
        tw_role: string().optional(),
      }),
    ),
  })

  const relationSchemas = {
    addressListSchema,
    emailSchema,
    organizationSchemas: {
      bankAccountSchema,
      generalDataOrganizationSchema,
      metaDataOrganizationSchema,
      refineMents: {
        bankAccountRefineMent,
      },
    },
    personSchemas: {
      generalDataPersonSchema,
      metaDataPersonSchema,
    },
    phoneNumberSchema,
    primaryAddressSchema,
    refineMents: {
      addressRefinement,
    },
    singleAddressSchema,
    weblinkSchema,
  }

  const commonSchemas = {
    dynamicLinkListSchema,
  }

  // TODO: may not need to export "bankAccountRefineMent"?
  // see todo in "CreateOrganizationForm" and "SectionBankAccountSelfUpdateOrganization"
  return { bankAccountRefineMent, commonSchemas, relationSchemas }
})

export { useZodSchemaStore }
